import React, { useEffect, useMemo, useState } from 'react';
import { Fade, Grid, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useSelector } from 'react-redux';
import { routes } from '../../utils/routing';
import { createEcommerceItemData, GtagEventName, sendGtagEvent } from '../../utils/gtag';
import EventCallToAction from '../event/EventCallToAction';
import EventSummary from '../event/EventSummary';
import IconPin from '../svg/icon-pin.svg';
import { formatDuration, formatPriceStart, formatTimeDuration } from '../../utils/format';
import { getSessionsGroups } from '../../utils/transform';
import EventPassportRequiredCard from './EventPassportRequiredCard';
import EventPassportRequiredInfoContainer from './EventPassportRequiredInfoContainer';
import useCustomOrgShowTime from '../../hooks/useCustomOrgShowTime';
import { getGettingPassportInitiate, removeGettingPassportInitiate } from './utils';
import { useAuthProps } from '../../hooks';
import useModal from '../../hooks/useModal';
import JLivePassportVerificationModal from '../JLivePassport/JLivePassortVerificationModal';
import { AuthSelectors, UserSelectors } from '../../store/selectors';

interface IProps {
  event: any;
  hasUserInternalPassport?: boolean;
  onJLivePassportCreated: () => void;
  onSuccessfullyProceed: () => void;
  hideTime?: boolean;
  goToTicketsStep: (passportCreated: boolean) => void;
  passportsFetched?: boolean;
}

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const isTestEnv = () => BUILD_ENV === 'test';
const isProductionEnv = () => BUILD_ENV === 'production';

const useStyles = makeStyles()((theme) => ({
  rightMenu: {
    width: 352,
    position: 'sticky',
    borderBottomRightRadius: 8,
    borderBottomLeftRadius: 8,
    backgroundColor: 'rgba(233, 235, 242, 1)',
    boxShadow: '0px 8px 32px rgba(34, 38, 82, 0.16)',
    zIndex: 100,
  },

  menuContainer: {
    backgroundColor: '#fff',
    borderBottomRightRadius: 8,
    borderBottomLeftRadius: 8,
  },

  summaryWrapper: {
    padding: 40,
  },

  topBotMenu: {
    textTransform: 'uppercase',
    position: 'fixed',
    left: 0,
    top: 64,
    '@media (max-width: 1187px)': {
      top: 'unset',
      bottom: 0,
      boxShadow: '0px 2px 4px black',
    },
    height: 80,
    width: '100%',
    color: 'white',
    zIndex: 200,
  },

  requiredPassportMobile: {
    height: 128,
  },

  timezone: {
    color: 'white',
  },
  requiredPassportContainer: {
    marginTop: 8,
    backgroundColor: theme.palette.custom.gray.extraLight,
    borderRadius: 8,
    padding: '24px 12px 12px 12px',
  },
  smallPad: {
    '@media (min-width:600px) and (max-width:790px)': {
      height: 80,
    },
  },
}));

const EventActionMenu: React.FC<React.PropsWithChildren<IProps>> = ({
  event,
  hasUserInternalPassport,
  onJLivePassportCreated,
  onSuccessfullyProceed,
  goToTicketsStep,
  passportsFetched,
}) => {
  const {
    id,
    min_ticket_price,
    max_ticket_price,
    default_currency,
    cta_button_label_display,
    settings,
    tickets_sale_status,
    has_tickets,
    address,
    start_at,
    end_at,
    timezone,
    sessions,
    organization,
    hide_date,
    hide_time,
  } = event;
  const { classes, cx } = useStyles();
  const [topGap, setTopGap] = useState(80);
  const time = start_at && end_at && formatTimeDuration(start_at, end_at, timezone);
  const duration = start_at && end_at && formatDuration(start_at, end_at, timezone);
  const [t] = useTranslation('public');
  const less1187 = useMediaQuery('(max-width: 1187px)');
  const less790 = useMediaQuery('(max-width: 790px)');
  const less930 = useMediaQuery('(max-width: 930px)');
  const [defaultTopGap] = useState<number | string>(80);
  const { customShowTime } = useCustomOrgShowTime(organization.id);
  const { isAuthenticated, isHardUser } = useAuthProps();
  const [showJLivePassportVerificationModal] = useModal(JLivePassportVerificationModal);
  const user = useSelector(AuthSelectors.currentUser);
  const userJLivePassportsState = useSelector(UserSelectors.userPassportsState);

  useEffect(() => {
    if (isAuthenticated && isHardUser && userJLivePassportsState.isFetched) {
      const gettingPassportInitiate = getGettingPassportInitiate();
      if (gettingPassportInitiate) {
        removeGettingPassportInitiate();
        if (!hasUserInternalPassport) {
          showJLivePassportVerificationModal({
            onSuccess: () => onJLivePassportCreated(),
            variant: user?.is_phone_verified ? 'profileUpdates' : 'phone',
          });
        } else {
          goToTicketsStep(false);
        }
      }
    }
  }, [
    isAuthenticated,
    isHardUser,
    onJLivePassportCreated,
    showJLivePassportVerificationModal,
    user?.is_phone_verified,
    hasUserInternalPassport,
    goToTicketsStep,
    passportsFetched,
    userJLivePassportsState.isFetched,
  ]);

  const price = useMemo(() => {
    if (id === 6047 && isProductionEnv() && !min_ticket_price && !max_ticket_price) return '';
    if (id === 1953 && isTestEnv() && !min_ticket_price && !max_ticket_price) return '';
    return formatPriceStart(min_ticket_price || 0, max_ticket_price || 0, default_currency, true, true);
  }, [default_currency, id, max_ticket_price, min_ticket_price]);

  const timesLabel = () => {
    if (Array.isArray(sessions) && sessions.length > 1) {
      return getSessionsGroups(sessions, timezone).length > 1
        ? t<string>('common:general.multipleDays')
        : t<string>('common:general.multipleTimes');
    }
    return (
      <>
        {time?.split('|')[0]} <span className={classes.timezone}>{time?.split('|')[1]}</span>
      </>
    );
  };

  useEffect(() => {
    const handleScroll = () => {
      const rigthMenu = document.getElementById('rightMenu');
      if (rigthMenu) {
        const distanceToTop = rigthMenu.getBoundingClientRect().top;
        setTopGap(distanceToTop);
      }
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const addressBlock = useMemo(() => {
    if (address?.city || address?.state)
      return (
        <>
          <Grid container style={{ minHeight: 34, paddingTop: 8, paddingLeft: 12 }} spacing={1}>
            <Grid item>
              <IconPin />
            </Grid>

            <Grid item xs>
              {address?.address_name ? (
                <Typography variant="body2" style={{ minHeight: 20, fontWeight: 600 }}>
                  {address?.address_name}
                </Typography>
              ) : (
                <Typography variant="body2" color="primary">
                  {address?.address1} {address?.address1 && <br />}
                  {address?.address2}
                  {address?.address2 && <br />}
                  {address?.city && `${address?.city}, `} {address?.state}
                  {address?.postal_code && `, ${address?.postal_code}`}
                </Typography>
              )}
            </Grid>
          </Grid>
          {address?.address_name && (
            <Typography variant="body2" color="primary" style={{ paddingLeft: 32 }}>
              {address?.address1} {address?.address1 && <br />}
              {address?.address2}
              {address?.address2 && <br />}
              {address?.city && `${address?.city}, `} {address?.state}
              {address?.postal_code && `, ${address?.postal_code}`}
            </Typography>
          )}
        </>
      );
    return <div />;
  }, [
    address?.address1,
    address?.address2,
    address?.address_name,
    address?.city,
    address?.postal_code,
    address?.state,
  ]);

  const durationSameDay = start_at && isSameDay(utcToZonedTime(start_at, timezone), utcToZonedTime(end_at, timezone));
  const showDuration = useMemo(() => {
    if (event.settings.internal_passport_required && hasUserInternalPassport && !less930) return duration;
    if (durationSameDay && less790) return null;
    if (!durationSameDay && less930) return null;
    return duration;
  }, [duration, durationSameDay, event.settings.internal_passport_required, hasUserInternalPassport, less790, less930]);

  const showActionButton = useMemo(() => {
    if (settings.internal_passport_required) return hasUserInternalPassport;
    return true;
  }, [hasUserInternalPassport, settings.internal_passport_required]);

  const showInternalPassportRequired = useMemo(
    () => settings.internal_passport_required && hasUserInternalPassport,
    [hasUserInternalPassport, settings.internal_passport_required],
  );

  return (
    <>
      {!less1187 && (
        <Fade in={!less1187 && topGap >= 80}>
          <div
            id="reels"
            style={{ position: 'relative', paddingTop: 54, marginLeft: 'auto', marginRight: 80, width: 352 }}
          >
            <div id="rightMenu" className={classes.rightMenu} style={{ top: defaultTopGap }}>
              <Grid className={classes.menuContainer} container>
                <Grid className={classes.summaryWrapper} item xs={12}>
                  <EventSummary event={event} color="primary" />
                  {addressBlock}
                </Grid>
                <Grid item xs={12}>
                  <EventCallToAction
                    position="unset"
                    borderRadius="0 0 8px 8px"
                    eventId={id}
                    hasTickets={has_tickets}
                    minPrice={min_ticket_price}
                    maxPrice={max_ticket_price}
                    currency={default_currency}
                    ticketsSaleStatus={tickets_sale_status}
                    ctaButtonLabelDisplay={cta_button_label_display}
                    dontShowActionButton={!showActionButton}
                    hideDate={hide_date}
                    hideTime={hide_time}
                    {...(!(settings?.use_third_party_link && settings?.third_party_link)
                      ? { href: routes.eventTickets(id).link }
                      : {
                          href: settings.third_party_link,
                          target: '_blank',
                          onClick: () => {
                            sendGtagEvent(GtagEventName.add_to_cart, createEcommerceItemData(event));
                          },
                        })}
                  />
                </Grid>
              </Grid>
              {showInternalPassportRequired && <EventPassportRequiredInfoContainer />}
              {!showActionButton && (
                <EventPassportRequiredCard
                  onSuccessfullyProceed={onSuccessfullyProceed}
                  onJLivePassportCreated={onJLivePassportCreated}
                  price={price}
                />
              )}
            </div>
          </div>
        </Fade>
      )}
      <Fade in={less1187 || topGap < 80}>
        <div
          className={cx(classes.topBotMenu, {
            [classes.requiredPassportMobile]: !showActionButton && less790,
            [classes.smallPad]: !showActionButton,
          })}
        >
          {showActionButton ? (
            <EventCallToAction
              time={customShowTime && timesLabel()}
              duration={showDuration}
              height={80}
              position="absolute"
              eventId={id}
              hasTickets={has_tickets}
              minPrice={min_ticket_price}
              maxPrice={max_ticket_price}
              currency={default_currency}
              ticketsSaleStatus={tickets_sale_status}
              ctaButtonLabelDisplay={cta_button_label_display}
              dontShowActionButton={!showActionButton}
              info={showInternalPassportRequired && <EventPassportRequiredInfoContainer bgColor="#D1DFFF" mt={0} />}
              hideDate={hide_date}
              hideTime={hide_time}
              {...(!(settings?.use_third_party_link && settings?.third_party_link)
                ? { href: routes.eventTickets(id).link }
                : {
                    href: settings.third_party_link,
                    target: '_blank',
                    onClick: () => {
                      sendGtagEvent(GtagEventName.add_to_cart, createEcommerceItemData(event));
                    },
                  })}
            />
          ) : (
            <EventPassportRequiredCard
              event={event}
              onSuccessfullyProceed={onSuccessfullyProceed}
              collapsed={topGap < 80 && !less1187}
              onJLivePassportCreated={onJLivePassportCreated}
              price={price}
            />
          )}
        </div>
      </Fade>
    </>
  );
};

export default EventActionMenu;
